import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Smartphone, Monitor, RotateCcw, ShoppingCart,
  Star, ChevronDown, ChevronLeft, ChevronRight, Search, Plus, Minus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export const ClientPreviewTab = () => {
  const [menu, setMenu] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [lang, setLang] = useState('fr');
  const [viewMode, setViewMode] = useState('phone');
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const catScrollRef = useRef(null);
  const [catScrollState, setCatScrollState] = useState({ canLeft: false, canRight: true });

  const updateCatScroll = useCallback(() => {
    const el = catScrollRef.current;
    if (!el) return;
    setCatScrollState({
      canLeft: el.scrollLeft > 4,
      canRight: el.scrollLeft < el.scrollWidth - el.clientWidth - 4
    });
  }, []);

  const scrollCats = (dir) => {
    const el = catScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 160, behavior: 'smooth' });
  };

  const handleCatWheel = useCallback((e) => {
    const el = catScrollRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [menuRes, catRes] = await Promise.all([
          axios.get(`${API}/menu?lang=${lang}`),
          axios.get(`${API}/categories?lang=${lang}`)
        ]);
        setMenu(menuRes.data);
        setCategories(catRes.data);
      } catch (e) { console.error(e); }
    };
    load();
  }, [lang]);

  const filteredMenu = useMemo(() => {
    let items = menu;
    if (activeCategory !== 'all') {
      items = items.filter(i => (i.category_id || i.category) === activeCategory);
    }
    if (search) {
      items = items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));
    }
    return items;
  }, [menu, activeCategory, search]);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.id === item.id);
      if (existing) return prev.map(c => c.id === item.id ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { id: item.id, name: item.name, price: item.price, qty: 1, image: item.image_url }];
    });
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.map(c => c.id === itemId ? { ...c, qty: c.qty - 1 } : c).filter(c => c.qty > 0));
  };

  const cartTotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  const frameWidth = viewMode === 'phone' ? 390 : 800;
  const frameHeight = viewMode === 'phone' ? 720 : 600;

  return (
    <div className="space-y-4" data-testid="client-preview-tab">
      {/* Controls */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#2D241D]">Apercu client</span>
          <span className="text-xs text-[#6E5E52]">— vue en temps reel du menu</span>
        </div>
        <div className="flex gap-1.5 ml-auto">
          <button onClick={() => setViewMode('phone')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === 'phone' ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#6E5E52]'}`}
            data-testid="preview-phone-btn">
            <Smartphone className="w-3.5 h-3.5" /> Mobile
          </button>
          <button onClick={() => setViewMode('desktop')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === 'desktop' ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#6E5E52]'}`}
            data-testid="preview-desktop-btn">
            <Monitor className="w-3.5 h-3.5" /> Desktop
          </button>
        </div>
        <select value={lang} onChange={(e) => setLang(e.target.value)}
          className="h-8 rounded-lg border border-[#E5DCD3] px-2 text-xs bg-white"
          data-testid="preview-lang-select">
          <option value="fr">Francais</option>
          <option value="en">English</option>
          <option value="th">Thai</option>
          <option value="ru">Russkiy</option>
          <option value="de">Deutsch</option>
          <option value="es">Espanol</option>
          <option value="ar">Arabic</option>
          <option value="zh">Chinese</option>
        </select>
        <button onClick={() => { setCart([]); setActiveCategory('all'); setSearch(''); }}
          className="px-3 py-1.5 rounded-lg bg-[#F3EDE5] text-xs text-[#6E5E52] hover:bg-[#E5DCD3] flex items-center gap-1"
          data-testid="preview-reset-btn">
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>

      {/* Phone frame */}
      <div className="flex justify-center">
        <div
          className={`relative bg-[#0D0D0D] overflow-hidden shadow-2xl ${viewMode === 'phone' ? 'rounded-[2.5rem] border-[6px] border-[#333]' : 'rounded-2xl border-2 border-[#E5DCD3]'}`}
          style={{ width: frameWidth, height: frameHeight }}
          data-testid="preview-frame"
        >
          {/* Phone notch */}
          {viewMode === 'phone' && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#333] rounded-b-2xl z-20" />
          )}

          {/* Simulated client app */}
          <div className="h-full overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
            {/* Header */}
            <div className="sticky top-0 z-10 bg-gradient-to-b from-[#0A422D] to-[#083323] px-4 pt-8 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-lg font-bold text-[#D4A853]">HIBISUN</h1>
                  <p className="text-[10px] text-[#D4A853]/60">Caribbean Cuisine - Patong</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-white/50 bg-white/10 px-2 py-0.5 rounded-full">{lang.toUpperCase()}</span>
                  <div className="relative">
                    <ShoppingCart className="w-5 h-5 text-[#D4A853]" />
                    {cartCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#E16833] text-white text-[9px] font-bold rounded-full flex items-center justify-center">{cartCount}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Search bar */}
              <div className="relative mt-2.5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un plat..."
                  className="w-full bg-white/10 text-white text-xs rounded-xl pl-9 pr-3 py-2 placeholder-white/30 border border-white/10 focus:outline-none focus:border-[#D4A853]/50" />
              </div>

              {/* Category pills */}
              <div className="relative mt-2.5 -mx-4 group/cats">
                {catScrollState.canLeft && (
                  <button onClick={() => scrollCats(-1)}
                    className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-[#0A422D]/90 border border-white/20 flex items-center justify-center opacity-0 group-hover/cats:opacity-100 transition-opacity"
                    aria-label="Scroll left">
                    <ChevronLeft className="w-3.5 h-3.5 text-white" />
                  </button>
                )}
                <div ref={catScrollRef}
                  onScroll={updateCatScroll}
                  onWheel={handleCatWheel}
                  className="flex gap-2 overflow-x-auto px-4 pb-2"
                  style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}>
                  <button onClick={() => setActiveCategory('all')}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors ${activeCategory === 'all' ? 'bg-[#D4A853] text-[#0D0D0D]' : 'bg-white/10 text-white/70'}`}>
                    Tout
                  </button>
                  {categories.map(cat => (
                    <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-medium whitespace-nowrap transition-colors ${activeCategory === cat.id ? 'bg-[#D4A853] text-[#0D0D0D]' : 'bg-white/10 text-white/70'}`}>
                      {cat.display_name || cat.name_fr || cat.name}
                    </button>
                  ))}
                  <div className="flex-shrink-0 w-4" aria-hidden="true" />
                </div>
                {catScrollState.canRight && (
                  <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-[#083323] to-transparent pointer-events-none" />
                )}
                {catScrollState.canRight && (
                  <button onClick={() => scrollCats(1)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-[#0A422D]/90 border border-white/20 flex items-center justify-center opacity-0 group-hover/cats:opacity-100 transition-opacity"
                    aria-label="Scroll right">
                    <ChevronRight className="w-3.5 h-3.5 text-white" />
                  </button>
                )}
              </div>
            </div>

            {/* Menu items */}
            <div className="px-3 py-3 space-y-2.5">
              {filteredMenu.map(item => {
                const inCart = cart.find(c => c.id === item.id);
                return (
                  <div key={item.id}
                    className="flex gap-3 bg-[#1A1A1A] rounded-2xl p-3 border border-white/5">
                    <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-[#2A2A2A]">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20 text-xl">?</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-white text-sm font-semibold line-clamp-1">
                            {item.name} {item.spice_level > 0 && <span className="text-[10px]">{'🌶'.repeat(item.spice_level)}</span>}
                          </h3>
                          {item.is_popular && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] text-[#D4A853]">
                              <Star className="w-2.5 h-2.5 fill-current" /> Populaire
                            </span>
                          )}
                        </div>
                      </div>
                      {item.description && (
                        <p className="text-white/40 text-[10px] line-clamp-2 mt-0.5">{item.description}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[#E16833] font-bold text-sm">{item.price} THB</span>
                        {inCart ? (
                          <div className="flex items-center gap-2">
                            <button onClick={() => removeFromCart(item.id)}
                              className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                              <Minus className="w-3 h-3 text-white" />
                            </button>
                            <span className="text-white text-xs font-bold min-w-[16px] text-center">{inCart.qty}</span>
                            <button onClick={() => addToCart(item)}
                              className="w-6 h-6 rounded-full bg-[#E16833] flex items-center justify-center">
                              <Plus className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => addToCart(item)}
                            className="px-3 py-1 rounded-full bg-[#E16833] text-white text-[10px] font-semibold">
                            Ajouter
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredMenu.length === 0 && (
                <div className="text-center py-12 text-white/30 text-sm">Aucun plat</div>
              )}
            </div>

            {/* Cart bar at bottom */}
            {cartCount > 0 && (
              <div className="sticky bottom-0 bg-[#0D0D0D] border-t border-white/10 px-4 py-3">
                <div className="bg-[#E16833] rounded-2xl px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-white" />
                    <span className="text-white text-xs font-semibold">{cartCount} article{cartCount > 1 ? 's' : ''}</span>
                  </div>
                  <span className="text-white font-bold">{cartTotal.toLocaleString()} THB</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-[#6E5E52]">
        Ceci est un apercu. Les donnees affichees sont en temps reel depuis votre menu.
      </p>
    </div>
  );
};
